<template>
  <div class="shift-calendar">
    <el-calendar v-model="calendarDate">
      <template #header>
        <div class="shift-calendar-header">
          <div class="shift-calendar-title">{{ calendarTitle }}</div>
          <div class="shift-calendar-nav">
            <button type="button" class="shift-calendar-nav-btn" @click="goToPreviousMonth">
              Предыдущий месяц
            </button>
            <button type="button" class="shift-calendar-nav-btn" @click="goToToday">
              Сегодня
            </button>
            <button type="button" class="shift-calendar-nav-btn" @click="goToNextMonth">
              Следующий месяц
            </button>
          </div>
        </div>
      </template>
      <template #date-cell="{ data }">
        <div class="calendar-date-click-area">
          <div class="calendar-popover-trigger-wrapper">
            <template v-if="mode === 'view'">
              <el-popover
                v-if="hasShift(data.date)"
                placement="top"
                trigger="manual"
                :width="340"
                :visible="activeDate === formatDate(data.date)"
                :teleported="false"
                @after-leave="activeDate = null"
                popper-class="shift-day-popover">
                <template #reference>
                  <div
                    class="calendar-popover-trigger calendar-date-click-area"
                    :class="{ active: activeDate === formatDate(data.date) }"
                    @click.stop="onViewDayClick(data)"
                  >
                    <div class="calendar-date-inner">
                      <div class="cell-date">{{ data.date.getDate() }}</div>
                      <div v-if="getShiftTime(data.date)" class="cell-shift-inline">
                        {{ getShiftTime(data.date) }}
                      </div>
                    </div>
                  </div>
                </template>

                <div class="shift-day-popover-card">
                  <div class="shift-day-popover-title">Режим</div>
                  <div class="shift-day-popover-time">
                    <span class="shift-day-popover-clock">◷</span>
                    <span>{{ getShiftTime(data.date) }}</span>
                  </div>
                  <div v-if="getBreaks(data.date).length" class="shift-day-popover-breaks">
                    <div
                      v-for="(br, idx) in getBreaks(data.date)"
                      :key="idx"
                      class="shift-day-popover-break-row"
                    >
                      <span class="shift-day-popover-break-label">
                        {{ (br.start_time || br.start)?.slice(0, 5) }}–{{ (br.end_time || br.end)?.slice(0, 5) }}
                      </span>
                    </div>
                  </div>
                </div>
              </el-popover>

              <div v-else class="calendar-popover-trigger">
                <div class="calendar-date-inner">
                  <div class="cell-date">{{ data.date.getDate() }}</div>
                </div>
              </div>
            </template>

            <template v-else-if="mode === 'edit'">
              <el-popover
                v-if="isSelected(data.date)"
                placement="top"
                trigger="manual"
                :width="340"
                :visible="activeDate === formatDate(data.date)"
                :teleported="false"
                @after-leave="activeDate = null"
                popper-class="shift-day-popover"
                :class="{ disabled: !workingHours }">
                <template #reference>
                  <div class="calendar-date-click-area"
                       :class="{
                         selected: isSelected(data.date),
                         active: activeDate === formatDate(data.date),
                         disabled: !workingHours || isPast(data.date)
                       }"
                       @click.stop="onDayClick(data)">
                    <div class="calendar-date-inner">
                      <div class="cell-date">{{ data.date.getDate() }}</div>
                      <div v-if="getShiftTime(data.date)" class="cell-shift-inline">
                        {{ getShiftTime(data.date) }}
                      </div>
                    </div>
                  </div>
                </template>
                <div class="shift-day-popover-card">
                  <div class="shift-day-popover-title">Режим</div>
                  <div class="shift-day-popover-time">
                    <span class="shift-day-popover-clock">◷</span>
                    <span>{{ getShiftTime(data.date) }}</span>
                  </div>
                  <div v-if="getBreaks(data.date).length" class="shift-day-popover-breaks">
                    <div
                      v-for="(br, idx) in getBreaks(data.date)"
                      :key="idx"
                      class="shift-day-popover-break-row"
                    >
                      <span class="shift-day-popover-break-label">
                        {{ (br.start_time || br.start)?.slice(0, 5) }}–{{ (br.end_time || br.end)?.slice(0, 5) }}
                      </span>
                      <el-button
                        class="shift-day-popover-break-remove"
                        size="small"
                        :icon="Delete"
                        @click="removeBreak(activeShift, idx)"
                      />
                    </div>
                  </div>
                  <div class="shift-day-popover-actions">
                    <el-tooltip content="Изменить время" placement="top">
                      <el-button class="shift-day-popover-action" size="small" :icon="Edit" @click="openEditShift(activeShift)"/>
                    </el-tooltip>
                    <el-tooltip content="Добавить перерыв" placement="top">
                      <el-button class="shift-day-popover-action" size="small" :icon="Plus" @click="openBreakDialog(activeShift)"/>
                    </el-tooltip>
                    <el-tooltip content="Удалить смену" placement="top">
                      <el-button class="shift-day-popover-action is-danger" size="small" :icon="Delete" @click="removeShift(formatDate(data.date))"/>
                    </el-tooltip>
                  </div>
                </div>
              </el-popover>
              <div v-else class="calendar-date-click-area"
                   :class="{ disabled: !workingHours || isPast(data.date) }"
                   @click.stop="onDayClick(data)">
                <div class="calendar-date-inner">
                  <div class="cell-date">{{ data.date.getDate() }}</div>
                  <div v-if="getShiftTime(data.date)" class="cell-shift-inline">
                    {{ getShiftTime(data.date) }}
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </template>
    </el-calendar>

    <el-dialog title="Изменить время смены" v-model="editDialogVisible" width="400px">
      <div style="display:flex; gap: 12px;">
        <el-time-select
          v-model="editStart"
          placeholder="Начало"
          step="00:15"
          :start="minWorkingHour"
          :end="maxWorkingHour"
          :max-time="editEnd || maxWorkingHour"
        />
        <el-time-select
          v-model="editEnd"
          placeholder="Конец"
          step="00:15"
          :start="minWorkingHour"
          :end="maxWorkingHour"
          :min-time="editStart || minWorkingHour"
        />
      </div>
      <template #footer>
        <el-button @click="editDialogVisible = false">Отмена</el-button>
        <el-button type="primary" @click="saveEditShift">Сохранить</el-button>
      </template>
    </el-dialog>

    <el-dialog title="Добавить перерыв" v-model="breakDialogVisible" width="400px">
      <el-form label-width="120px">
        <el-form-item label="Тип">
          <el-radio-group v-model="breakType">
            <el-radio-button label="lunch">🍽️</el-radio-button>
            <el-radio-button label="break">⏸️</el-radio-button>
            <el-radio-button label="maintenance">🛠️</el-radio-button>
            <el-radio-button label="handover">🔁</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="Начало">
          <el-select v-model="breakStart" placeholder="Начало" filterable style="width: 100%">
            <el-option
              v-for="opt in timeOptions"
              :key="'s-' + opt"
              :label="opt"
              :value="opt"
              :disabled="isStartDisabled(opt)"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="Конец">
          <el-select v-model="breakEnd" placeholder="Конец" filterable style="width: 100%">
            <el-option
              v-for="opt in timeOptions"
              :key="'e-' + opt"
              :label="opt"
              :value="opt"
              :disabled="isEndDisabled(opt)"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="breakDialogVisible = false">Отмена</el-button>
        <el-button type="primary" @click="saveBreak">Сохранить</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { Delete, Edit, Plus } from '@element-plus/icons-vue'

export default {
  name: 'ShiftCalendar',
  computed: {
    Delete() {
      return Delete
    },
    Plus() {
      return Plus
    },
    Edit() {
      return Edit
    },
    minWorkingHour() {
      return this.workingHours ? (this.workingHours.start_time || this.workingHours.start) : '00:00'
    },
    maxWorkingHour() {
      return this.workingHours ? (this.workingHours.end_time || this.workingHours.end) : '23:59'
    },
    disabledBreakTimes() {
      if (!this.breakShift?.breaks) return []
      return this.breakShift.breaks.map(b => [b.start, b.end])
    },
    timeOptions() {
      const res = []
      const start = (this.minWorkingHour || '00:00').slice(0,5)
      const end = (this.maxWorkingHour || '23:59').slice(0,5)
      const toMin = t => {
        const [h,m] = t.split(':').map(Number); return h*60+m
      }
      const toStr = m => `${String(Math.floor(m/60)).padStart(2,'0')}:${String(m%60).padStart(2,'0')}`
      let cur = toMin(start)
      const stop = toMin(end)
      while (cur <= stop) { res.push(toStr(cur)); cur += 15 }
      return res
    },
    calendarTitle() {
      const formatter = new Intl.DateTimeFormat('ru-RU', {
        month: 'long',
        year: 'numeric'
      })
      const parts = formatter.formatToParts(this.calendarDate)
      const month = parts.find(part => part.type === 'month')?.value || ''
      const year = parts.find(part => part.type === 'year')?.value || ''
      return `${month.charAt(0).toUpperCase()}${month.slice(1)} ${year}`
    }
  },
  props: {
    shifts: {
      type: Array,
      required: true,
    },
    mode: {
      type: String,
      default: 'view',
      validator(value) {
        return ['view', 'edit'].includes(value);
      }
    },
    workingHours: {
      type: Object,
      required: false,
      default: null,
    },
    selectedDates: {
      type: Array,
      required: false,
      default: () => [],
    },
    userName: { type: String, default: '' }
  },
  emits: ['update:selectedDates', 'update:shifts'],
  data() {
    return {
      calendarDate: new Date(),
      activeShift: null,
      activeDate: null,
      internalShifts: [],
      editDialogVisible: false,
      editShift: null,
      editStart: '',
      editEnd: '',
      breakDialogVisible: false,
      breakShift: null,
      breakType: 'lunch',
      breakStart: '',
      breakEnd: ''
    }
  },
  watch: {
    shifts: {
      immediate: true,
      deep: true,
      handler (val) {
        this.internalShifts = (Array.isArray(val) ? val : []).map(shift => {
          const start = shift.start_time || shift.start
          const end = shift.end_time || shift.end

          return {
            ...shift,
            start_time: start,
            end_time: end,
            start,
            end,
            breaks: Array.isArray(shift.breaks)
              ? shift.breaks.map(b => {
                  const bStart = b.start_time || b.start
                  const bEnd = b.end_time || b.end
                  return {
                    ...b,
                    start: bStart,
                    end: bEnd
                  }
                })
              : []
          }
        })
      }
    }
  },
  mounted() {
  },
  methods: {
    isStartDisabled(t) {
      const time = t.slice(0, 5)
      // запрещаем ставить начало внутри существующих перерывов
      if ((this.disabledBreakTimes || []).some(([s, e]) => time >= s && time < e)) return true
      // запрещаем если конец выбран раньше
      if (this.breakEnd && time >= this.breakEnd) return true
      return false
    },
    isEndDisabled(t) {
      const time = t.slice(0, 5)
      // конец не может быть внутри существующих перерывов
      if ((this.disabledBreakTimes || []).some(([s, e]) => time > s && time <= e)) return true
      // конец не может быть раньше начала
      if (!this.breakStart) return true
      if (time <= this.breakStart) return true
      return false
    },
    formatDate(date) {
      return date.getFullYear() + '-' +
             String(date.getMonth() + 1).padStart(2, '0') + '-' +
             String(date.getDate()).padStart(2, '0');
    },
    goToPreviousMonth() {
      this.calendarDate = new Date(this.calendarDate.getFullYear(), this.calendarDate.getMonth() - 1, 1)
    },
    goToToday() {
      this.calendarDate = new Date()
    },
    goToNextMonth() {
      this.calendarDate = new Date(this.calendarDate.getFullYear(), this.calendarDate.getMonth() + 1, 1)
    },
    hasShift(date) {
      const dStr = this.formatDate(date);
      return this.internalShifts.some(s => s.date === dStr);
    },
    getBreaks(date) {
      const dStr = this.formatDate(date);
      const shift = this.internalShifts.find(s => s.date === dStr);
      return shift && Array.isArray(shift.breaks) ? shift.breaks : [];
    },
    getShiftTime(date) {
      const dStr = this.formatDate(date);
      const shift = this.internalShifts.find(s => s.date === dStr);
      if (shift && shift.start_time && shift.end_time) {
        return `${shift.start_time.slice(0, 5)}–${shift.end_time.slice(0, 5)}`;
      }
      return null;
    },
    isSelected(date) {
      const dStr = this.formatDate(date);
      return this.internalShifts.some(s => s.date === dStr);
    },
    isPast(date) {
      const dStr = this.formatDate(date);
      const todayStr = this.formatDate(new Date());
      return dStr < todayStr;
    },
    onDayClick(data) {
      // data: { date: Date, type: string, ... }
      const panelYear = this.calendarDate.getFullYear();
      const panelMonth = this.calendarDate.getMonth();
      if (!this.workingHours) {
        this.$message?.warning?.('Сначала выберите локацию с рабочим режимом')
        return
      }
      const date = data.date;
      const dStr = this.formatDate(date);
      const todayStr = this.formatDate(new Date());
      if (dStr < todayStr) {
        this.$message?.warning?.('Нельзя создавать/редактировать смены в прошлом. Доступно с сегодняшнего дня');
        return;
      }
      const idx = this.internalShifts.findIndex(s => s.date === dStr);
      if (idx === -1) {
        // Первый клик: создаём смену и подсвечиваем день
        const newShift = {
          date: dStr,
          start_time: this.workingHours?.start_time || this.workingHours?.start || '09:00',
          end_time: this.workingHours?.end_time || this.workingHours?.end || '23:59',
          breaks: []
        };
        const newShifts = [...this.internalShifts, newShift];
        this.internalShifts = newShifts;
        this.$emit('update:shifts', newShifts);
        this.activeDate = dStr;
        this.activeShift = newShift;
      } else {
        // Повторный клик: открыть/закрыть поповер управления
        this.activeShift = this.internalShifts[idx];
        this.activeDate = (this.activeDate === dStr) ? null : dStr;
      }
      if (data.type === 'prev-month' || data.type === 'next-month') {
        this.$nextTick(() => {
          this.calendarDate = new Date(panelYear, panelMonth, 1);
        });
      }
    },
    onViewDayClick(data) {
      const dStr = this.formatDate(data.date)
      this.activeDate = this.activeDate === dStr ? null : dStr
      this.$emit('day-click', data)
    },
    removeShift(dStr) {
      const todayStr = this.formatDate(new Date());
      if (dStr < todayStr) {
        this.$message?.warning?.('Нельзя изменять прошедшие смены');
        return;
      }
      this.$confirm('Удалить эту смену?', 'Подтверждение', {
        confirmButtonText: 'Удалить',
        cancelButtonText: 'Отмена',
        type: 'warning',
      }).then(() => {
        const newShifts = this.internalShifts.filter(s => s.date !== dStr)
        this.internalShifts = newShifts
        this.$emit('update:shifts', newShifts)
        this.activeDate = null
      }).catch(() => {})
    },
    openEditShift(shift) {
      if (this.isPast(new Date(shift.date))) {
        this.$message?.warning?.('Нельзя редактировать прошедшие смены');
        return;
      }
      this.editShift = shift
      this.editStart = shift.start_time
      this.editEnd = shift.end_time
      this.editDialogVisible = true
    },
    saveEditShift() {
      if (!this.editShift) return
      this.editShift.start_time = this.editStart
      this.editShift.end_time = this.editEnd
      this.$emit('update:shifts', this.internalShifts)
      this.editDialogVisible = false
      this.editShift = null
    },
    openBreakDialog(shift) {
      if (this.isPast(new Date(shift.date))) {
        this.$message?.warning?.('Нельзя добавлять перерывы в прошедшие смены');
        return;
      }
      // Всегда берем свежую ссылку из internalShifts
      this.breakShift = this.internalShifts.find(s => s.date === shift.date) || shift
      this.breakType = 'lunch'
      this.breakStart = ''
      this.breakEnd = ''
      this.breakDialogVisible = true
    },
    saveBreak() {
      if (!this.breakShift) return
      if (!this.breakStart || !this.breakEnd) {
        this.$message?.warning?.('Укажите время перерыва')
        return
      }

      // Нормализация времени к HH:MM
      const toHM = (t) => (t || '').slice(0, 5)
      const nStart = toHM(this.breakStart)
      const nEnd = toHM(this.breakEnd)

      // Проверка пересечения с существующими перерывами
      const existing = this.breakShift.breaks || []
      const overlaps = existing.some(b => {
        const startB = toHM(b.start || b.start_time)
        const endB = toHM(b.end || b.end_time)
        return (nStart < endB && nEnd > startB)
      })
      if (overlaps) {
        this.$message?.warning?.('Выбранное время пересекается с существующим перерывом')
        return
      }

      const newBreak = {
        date: this.breakShift.date,
        type: this.breakType,
        start: nStart,
        end: nEnd
      }

      const updatedShifts = this.internalShifts.map(s => {
        if (s.date === this.breakShift.date) {
          return {
            ...s,
            breaks: [...(s.breaks || []), newBreak]
          }
        }
        return s
      })

      this.internalShifts = updatedShifts
      this.$emit('update:shifts', updatedShifts)

      // Обновляем активную смену на свежую ссылку, чтобы следующее добавление видело актуальные перерывы
      this.activeShift = this.internalShifts.find(s => s.date === this.breakShift.date) || null

      this.breakDialogVisible = false
      this.breakShift = null
    },
    removeBreak(shift, idx) {
      this.$confirm('Удалить этот перерыв?', 'Подтверждение', {
        confirmButtonText: 'Удалить',
        cancelButtonText: 'Отмена',
        type: 'warning',
      }).then(() => {
        const updatedShifts = this.internalShifts.map(s => {
          if (s.date === shift.date) {
            const newBreaks = [...s.breaks]
            newBreaks.splice(idx, 1)
            return { ...s, breaks: newBreaks }
          }
          return s
        })
        this.internalShifts = updatedShifts
        this.$emit('update:shifts', updatedShifts)
      }).catch(() => {
      })
    }
  }
};
</script>
<style scoped>
.shift-calendar {
  width: 100%;
}

.shift-calendar-header {
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  gap: 18px;
  width: 100%;
  flex-wrap: nowrap;
}

.shift-calendar-title {
  color: var(--cal-text-inverse);
  font-size: 28px;
  line-height: 1;
  white-space: nowrap;
  text-shadow: 0 2px 0 rgba(5, 12, 28, 0.55);
  flex: 0 0 auto;
}

.shift-calendar-nav {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 22px;
  margin-left: auto;
  padding-bottom: 2px;
  flex: 0 0 auto;
  white-space: nowrap;
}

.shift-calendar-nav-btn {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--cal-text-inverse);
  font: inherit;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  opacity: 0.95;
}

.shift-calendar-nav-btn:hover,
.shift-calendar-nav-btn:focus,
.shift-calendar-nav-btn:focus-visible {
  color: #33a3ea;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 5px;
}

::v-deep(.el-calendar) {
  background: transparent;
  border: 0;
  color: var(--cal-text-inverse);
  --el-calendar-cell-width: 72px;
}

::v-deep(.el-calendar__header) {
  padding: 18px 0 16px;
  border-bottom: 0;
  background: transparent;
}

::v-deep(.el-calendar__body) {
  padding: 8px 0 0;
  background: transparent;
}

::v-deep(.el-calendar-table) {
  background: transparent;
}

::v-deep(.el-calendar-table thead),
::v-deep(.el-calendar-table tbody),
::v-deep(.el-calendar-table tr),
::v-deep(.el-calendar-table td) {
  background: transparent;
}

::v-deep(.el-calendar-table td),
::v-deep(.el-calendar-table tr:first-child td),
::v-deep(.el-calendar-table td.is-selected),
::v-deep(.el-calendar-table td.next),
::v-deep(.el-calendar-table td.prev) {
  border: 0 !important;
}

.calendar-cell {
  max-height: 100px;
  overflow-y: auto;
}

.cell-break-time {
  line-height: 1.05;
  font-size: 10px;
}
.cell-shift-time {
  font-size: 11px;
  line-height: 1.2;
  font-weight: 500;
}
.cell-info {
  font-size: 10px;
  line-height: 1.1;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.calendar-popover-trigger {
  cursor: pointer;
  user-select: none;
}
.calendar-date-click-area {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: 100%;
  cursor: pointer;
  user-select: none;
}
.calendar-date-click-area.selected {
  color: var(--cal-text-inverse) !important;
}
.calendar-popover-trigger-wrapper {
  width: 100%;
  height: 100%;
}
.calendar-date-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
  padding: 6px 0 0;
}
.cell-date {
  margin-bottom: 2px;
}

.calendar-date-click-area.selected .calendar-date-inner {
  background: transparent;
  border-radius: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  justify-content: flex-start;
  padding: 6px 0 0;
}

/* Убираем стандартный hover эффект у el-calendar на уровне ячейки дня */
::v-deep(.el-calendar-day:hover) {
  background-color: transparent !important;
}

/* Убираем внутренние отступы у ячеек дня */
::v-deep(.el-calendar-day) {
  padding: 0 !important;
  display: flex;
  align-items: stretch;
  background: transparent !important;
  height: 72px !important;
  min-height: 72px !important;
}

/* Центрируем число и делаем его ровным */
.cell-date {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  font-weight: 400;
  font-size: 30px;
  line-height: 1;
  color: #f7f3ff;
  text-shadow: none;
}

::v-deep(.el-calendar-table td.is-selected) {
  background-color: transparent;
}

::v-deep(.el-calendar-table td) {
  color: #f7f3ff !important;
}

::v-deep(.el-calendar-table td .cell-date) {
  color: #f7f3ff !important;
}

::v-deep(.el-calendar-table td.prev),
::v-deep(.el-calendar-table td.next) {
  color: #091638 !important;
}

::v-deep(.el-calendar-table td.prev .cell-date),
::v-deep(.el-calendar-table td.next .cell-date) {
  color: #091638 !important;
  text-shadow: none;
}

::v-deep(.el-calendar-table td.is-today .cell-date) {
  color: #f7f3ff !important;
}

.calendar-date-click-area.selected .cell-date {
  color: #f7f3ff !important;
}

.calendar-date-click-area.active .cell-date {
  color: #55c7ff !important;
}

.cell-shift-inline {
  margin-top: 10px;
  color: #4baeff;
  font-size: 12px;
  line-height: 1;
  font-weight: 400;
  letter-spacing: 0;
  white-space: nowrap;
}

.shift-day-popover-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 18px 22px 14px;
  text-align: center;
}

.shift-day-popover-title {
  color: #f7f3ff;
  font-size: 28px;
  line-height: 1;
  font-weight: 400;
}

.shift-day-popover-time {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  color: #4baeff;
  font-size: 24px;
  line-height: 1;
  font-weight: 400;
}

.shift-day-popover-clock {
  font-size: 28px;
  line-height: 1;
}

.shift-day-popover-breaks {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgba(102, 140, 214, 0.18);
}

.shift-day-popover-break-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.shift-day-popover-break-label {
  color: rgba(247, 243, 255, 0.88);
  font-size: 13px;
  line-height: 1;
}

.shift-day-popover-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  margin-top: 14px;
}

.shift-day-popover-action,
.shift-day-popover-break-remove {
  min-width: 36px !important;
  height: 36px !important;
  padding: 0 10px !important;
  background: rgba(23, 46, 101, 0.9) !important;
  border: 1px solid rgba(93, 126, 198, 0.3) !important;
  border-radius: 8px !important;
  color: #eaf6ff !important;
}

.shift-day-popover-action.is-danger,
.shift-day-popover-break-remove {
  color: #ff8b8b !important;
}

.shift-day-popover-action:hover,
.shift-day-popover-action:focus,
.shift-day-popover-break-remove:hover,
.shift-day-popover-break-remove:focus {
  background: rgba(29, 57, 121, 0.96) !important;
  border-color: rgba(102, 144, 229, 0.44) !important;
}

.cell-shift-tag, .cell-break-tag {
  margin-top: 3px;
  font-weight: 600;
  display: flex;
  text-align: center;
  font-size: 9px;
  justify-content: flex-start;
  align-items: center;
}

/* Подсветка выходных дней (суббота и воскресенье) */
::v-deep(.el-calendar-table colgroup col:nth-child(7)),
::v-deep(.el-calendar-table colgroup col:nth-child(6)) {
  background-color: transparent;
}

::v-deep(.el-calendar-table td:nth-child(7) .el-calendar-day),
::v-deep(.el-calendar-table td:nth-child(6) .el-calendar-day) {
  background-color: transparent;
}

::v-deep(.el-calendar-table th) {
  background: transparent;
  border-bottom: 1px solid rgba(135, 190, 255, 0.28);
  color: var(--cal-text-inverse);
  font-size: 18px;
  font-weight: 400;
  line-height: 1;
  padding-bottom: 12px;
  text-shadow: 0 2px 0 rgba(5, 12, 28, 0.55);
}

::v-deep(.el-calendar-table th:nth-child(7)),
::v-deep(.el-calendar-table th:nth-child(6)) {
  background: transparent;
}

::v-deep(.el-popover) {
  max-width: 360px;
  white-space: normal;
}

::v-deep(.shift-day-popover.el-popper) {
  background: #0d1a46 !important;
  border: 0 !important;
  border-radius: 14px !important;
  box-shadow: 0 18px 36px rgba(4, 11, 30, 0.34) !important;
  padding: 0 !important;
}

::v-deep(.shift-day-popover.el-popper .el-popper__arrow:before) {
  background: #0d1a46 !important;
  border: 0 !important;
}

.calendar-date-click-area.disabled {
  pointer-events: none;
  opacity: 0.4;
}

.cell-break-icons {
  display: flex;
  flex-direction: row;
  gap: 2px;
  margin-top: 2px;
  font-size: 12px;
}
.break-icon {
  line-height: 1;
}

::v-deep(.el-dialog) {
  background: var(--appointment-panel);
  border: 1px solid var(--cal-border);
}

::v-deep(.el-dialog__title),
::v-deep(.el-form-item__label) {
  color: var(--cal-text-inverse);
}

::v-deep(.el-time-select .el-input__wrapper),
::v-deep(.el-select__wrapper),
::v-deep(.el-radio-button__inner) {
  background: var(--appointment-control);
  color: var(--cal-text-inverse);
  box-shadow: 0 0 0 1px var(--cal-border) inset;
  border-color: transparent;
}

::v-deep(.el-radio-button__original-radio:checked + .el-radio-button__inner) {
  background: var(--appointment-primary);
  color: var(--appointment-primary-text);
  box-shadow: 0 0 0 1px var(--cal-border) inset;
}

::v-deep(.el-dialog .el-button--default) {
  background: var(--appointment-secondary);
  border-color: transparent;
  color: var(--appointment-secondary-text);
}

::v-deep(.el-dialog .el-button--primary) {
  background: var(--appointment-primary);
  border-color: transparent;
  color: var(--appointment-primary-text);
}

@media (max-width: 860px) {
  .shift-calendar-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    flex-wrap: wrap;
  }

  .shift-calendar-nav {
    justify-content: flex-start;
    gap: 18px;
    margin-left: 0;
    white-space: normal;
    flex-wrap: wrap;
    padding-bottom: 0;
  }

  .shift-calendar-title {
    font-size: 24px;
  }
}

</style>
